import { t } from "../i18n";
import React, { useState, useCallback, MouseEvent } from "react";
import type { Editor as EditorType } from "../Editor";
import Block, {
  NewFromHtmlOptions,
  EditorOptions,
  SerializeOptions,
  HasBlocks,
  NewOptions,
} from "../Block";
import Column from "./Column";
import { useEditorContext } from "../Context";
import {
  parseContent,
  NO_BLOCK_TYPE_FALLBACK,
  escapeSingleQuoteAttribute,
} from "../util";
import icon from "../img/icon/columns.svg";
import BlockToolbar from "../Component/BlockToolbar";
import BlockToolbarButton from "../Component/BlockToolbarButton";
import BlockConfigPanel from "../Component/BlockConfigPanel";
import BlockSetupCommon from "../Component/BlockSetupCommon";

interface EditorProps extends EditorOptions {
  block: Columns;
}

const getSelectableColumns = (
  editor: EditorType
): {
  selectableColumns: number[];
  defaultColumns: number;
} => {
  const opts = editor.opts.block["core-columns"] || {};
  const selectableColumns = Array.isArray(opts.selectableColumns)
    ? opts.selectableColumns
    : [2, 3, 4];
  return {
    selectableColumns,
    defaultColumns: opts.defaultColumns || selectableColumns[0] || 2,
  };
};

const Editor: React.FC<EditorProps> = ({
  block,
  focus,
  focusBlock,
  canRemove,
}: EditorProps) => {
  const { editor } = useEditorContext();
  const { selectableColumns } = getSelectableColumns(editor);

  const [showConfigPanel, setConfigPanel] = useState(false);
  const toggleConfigPanel = useCallback(() => {
    setConfigPanel((prev) => !prev);
  }, []);

  const changeLayout = useCallback((ev: MouseEvent) => {
    const inputEl = ev.currentTarget.querySelector("input");
    if (!inputEl) {
      throw "error";
    }

    const layout = inputEl.value;
    if (layout === block.getColumnLayout()) {
      return;
    }

    const cols = parseInt(layout);
    const len = block.blocks.length;

    editor.editManager.beginGrouping();

    if (len < cols) {
      for (let i = len; i < cols; i++) {
        editor.addBlock(block, block.newColumn(), i);
      }
    } else {
      for (let i = len - cols; i > 0; i--) {
        editor.removeBlock(block, block.blocks[block.blocks.length - 1]);
      }
    }

    editor.editManager.endGrouping();

    setConfigPanel(false);
  }, []);

  const curLayout = block.getColumnLayout();
  return (
    <>
      <BlockSetupCommon block={block} keys={["className"]} />
      <div className="mt-be-columns" style={{ display: "flex" }}>
        {block.blocks.map((c) => c.editor({ focus, focusBlock, canRemove }))}
      </div>
      {selectableColumns.length > 1 && focus && canRemove && (
        <BlockToolbar>
          <BlockToolbarButton
            icon={icon}
            label={t("Edit")}
            onClick={toggleConfigPanel}
          />
        </BlockToolbar>
      )}
      <BlockConfigPanel in={focus && showConfigPanel}>
        <ul style={{ display: "flex", listStyle: "none" }}>
          {selectableColumns.map((columns: number) => {
            const columnString = columns.toString();
            return (
              <li key={columnString}>
                <label onClick={changeLayout}>
                  <input
                    type="radio"
                    value={columnString}
                    defaultChecked={curLayout === columnString}
                  />
                  {t("{{columns}} columns", { columns: columnString })}
                </label>
              </li>
            );
          })}
        </ul>
      </BlockConfigPanel>
    </>
  );
};

class Columns extends Block implements HasBlocks {
  public static typeId = "core-columns";
  public static selectable = true;
  public static icon = icon;
  public static get label(): string {
    return t("Columns");
  }

  public blocks: Column[];

  public constructor(init?: Partial<Columns>) {
    super();
    this.blocks = [this.newColumn(), this.newColumn()];
    if (init) {
      Object.assign(this, init);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async new(opts: NewOptions): Promise<Block> {
    const block = (await super.new(opts)) as Columns;
    const { defaultColumns } = getSelectableColumns(opts.editor);
    block.blocks = Array(defaultColumns)
      .fill(null)
      .map(() => block.newColumn());
    return block;
  }

  public newColumn(): Column {
    return new Column({ showShortcuts: false });
  }

  public getColumnLayout(): string {
    return `${this.blocks.length}`;
  }

  public childBlocks(): Block[] {
    return this.blocks;
  }

  public editor({ focus, focusBlock, canRemove }: EditorOptions): JSX.Element {
    return (
      <Editor
        key={this.id}
        block={this}
        focus={focus}
        focusBlock={focusBlock}
        canRemove={canRemove}
      />
    );
  }

  public html(): string {
    return `<div class="mt-be-columns${
      this.className ? ` ${this.className}` : ""
    }" style="display: flex">${this.blocks
      .map((c) => c.htmlString())
      .join("")}</div>`;
  }

  public async serialize(opts: SerializeOptions): Promise<string> {
    const m = opts.editor.serializeMeta(this, opts.external);
    const serializedColumns = await Promise.all(
      this.blocks.map((c) => c.serialize(opts))
    );
    return `<!-- mt-beb t="${(this.constructor as typeof Block).typeId}"${
      m ? ` m='${escapeSingleQuoteAttribute(m)}'` : ""
    } --><div class="mt-be-columns${
      this.className ? ` ${this.className}` : ""
    }" style="display: flex">${serializedColumns.join(
      ""
    )}</div><!-- /mt-beb -->`;
  }

  public static async newFromHtml({
    node,
    factory,
    meta,
    context,
  }: NewFromHtmlOptions): Promise<Block> {
    const blocks = (await parseContent(
      node.innerHTML,
      factory,
      context,
      NO_BLOCK_TYPE_FALLBACK
    )) as Column[];
    blocks.forEach((b) => (b.showShortcuts = false));
    return new Columns(Object.assign({ blocks }, meta));
  }
}

export default Columns;
